/**
 * What an effect draws. §6.4.
 *
 * Every effect here comes down to the object's own outline drawn again -
 * moved, recoloured, outlined, softened - behind or instead of the object
 * itself, or a box behind it. So an effect is written as a list of passes, in
 * drawing order, and the canvas, the SVG proof and the press PDF each draw the
 * same list their own way. That is the whole trick that keeps a glitch looking
 * the same on screen and on the card: there is one definition of what a
 * glitch is, and it is this file.
 *
 * A pass with blur is a halo. Ink on paper cannot be soft-edged without being
 * printed as a picture, so in the press file a halo becomes a 300 dpi image of
 * the one colour with the softness in its mask - the letters themselves stay
 * sharp vectors on top. Everything else is plain vector artwork.
 */

import type { Colour, Effect, EffectKind, Fill, Ink, Layer, ShapeLayer, TextLayer } from '../model/types';
import { isGradient } from '../model/types';
import { ptToMm } from '../model/units';

/** One thing to draw. Millimetres, in the layer's own frame. */
export interface Pass {
  /** The object's outline, or a box behind it. */
  source: 'outline' | 'box';
  /** Moved by this much. */
  dx: number;
  dy: number;
  fill: Fill | null;
  stroke: Colour | null;
  /** Stroke width, in millimetres of the layer - never of a glyph. */
  width: number;
  dash?: number[];
  /** 0 to 1. */
  alpha: number;
  /** Softness, as a Gaussian's standard deviation in millimetres. 0 is a hard edge. */
  blur: number;
  /** Laid down this many times over itself: how a glow is made denser without being made wider. */
  repeat: number;
  /** For a box: where, and how round. */
  box?: { x: number; y: number; w: number; h: number; r: number };
  /** The object itself, as opposed to something the effect added. */
  body?: boolean;
}

/** The object's own paint, before any effect touches it. */
interface Own {
  fill: Fill | null;
  stroke: Colour | null;
  width: number;
  dash?: number[];
}

const BLACK: Colour = { space: 'cmyk', value: [ 0, 0, 0, 1 ] };

/**
 * The passes for a layer, in drawing order.
 *
 * @param extent What the object covers - for text, the lines themselves
 *   rather than the box they sit in - for effects that surround it.
 */
export function plan( layer: TextLayer | ShapeLayer, own: Own, extent: { x: number; y: number; w: number; h: number } ): Pass[] {
  const body: Pass = { source: 'outline', dx: 0, dy: 0, alpha: 1, blur: 0, repeat: 1, body: true, ...own };
  const effect = layer.effect;

  if ( ! effect ) {
    const shadow = shadowPass( layer );

    return shadow ? [ shadow, body ] : [ body ];
  }

  const ref = reference( layer );
  const colour = effect.colour ?? BLACK;
  const alpha = clamp01( 1 - effect.transparency / 100 );

  const angle = ( effect.direction * Math.PI ) / 180;
  const reach = ( effect.offset / 100 ) * ref * 0.35;
  const dx = Math.cos( angle ) * reach;
  const dy = Math.sin( angle ) * reach;

  // Thickness of an outline, a hollow or a splice.
  const thick = Math.max( 0.05, ( effect.size / 100 ) * ref * 0.14 );

  const copy = ( more: Partial<Pass> ): Pass => ( {
    source: 'outline',
    dx: 0,
    dy: 0,
    fill: colour,
    stroke: null,
    width: 0,
    alpha: 1,
    blur: 0,
    repeat: 1,
    ...more,
  } );

  const ownColour = solid( own.fill ) ?? own.stroke ?? BLACK;

  switch ( effect.kind ) {
    case 'drop':
      return [ copy( { dx, dy, alpha, blur: ( effect.blur / 100 ) * ref * 0.3 } ), body ];

    case 'glow':
      // Laid twice, so it reads as a glow and not a smudge: a single Gaussian
      // is faint by the time it is wide enough to see.
      return [ copy( { alpha, blur: ( 0.04 + ( effect.size / 100 ) * 0.4 ) * ref, repeat: 2 } ), body ];

    case 'echo':
      // Two copies trailing away, the further one fainter.
      return [
        copy( { dx: dx * 2, dy: dy * 2, alpha: alpha * 0.35 } ),
        copy( { dx, dy, alpha: alpha * 0.7 } ),
        body,
      ];

    case 'outline':
      // A thick stroke behind the object, so the outline grows outward and the
      // letters keep their own weight.
      return [ copy( { stroke: colour, width: thick * 2 } ), body ];

    case 'background': {
      const pad = ( effect.size / 100 ) * ref * 0.8;
      const w = extent.w + pad * 2;
      const h = extent.h + pad * 2;

      return [
        copy( {
          source: 'box',
          alpha,
          box: { x: extent.x - pad, y: extent.y - pad, w, h, r: ( effect.roundness / 100 ) * ( Math.min( w, h ) / 2 ) },
        } ),
        body,
      ];
    }

    case 'splice':
      // A solid copy, moved, under a hollow version of the object.
      return [ copy( { dx, dy } ), { ...body, fill: null, stroke: ownColour, width: thick } ];

    case 'hollow':
      return [ { ...body, fill: null, stroke: ownColour, width: thick } ];

    case 'neon': {
      // The tube lit from inside: pale letters, and their own colour glowing round them.
      const blur = ( 0.06 + ( effect.size / 100 ) * 0.36 ) * ref;

      return [
        copy( { fill: ownColour, blur, repeat: 2 } ),
        copy( { fill: ownColour, blur: blur * 0.35 } ),
        { ...body, fill: pale( ownColour ), stroke: null, width: 0 },
      ];
    }

    case 'glitch':
      return [
        copy( { dx: -dx, dy: -dy, fill: colour } ),
        copy( { dx, dy, fill: effect.colour2 ?? { space: 'cmyk', value: [ 0, 1, 0, 0 ] } } ),
        body,
      ];

    default:
      return [ body ];
  }
}

/**
 * The size an effect's sliders are measured against.
 *
 * For text, the type size: a 50 on 12 pt and a 50 on 48 pt are the same shadow
 * in proportion, which is what a customer resizing a heading expects. For a
 * shape, a share of its shorter side.
 */
export function reference( layer: Layer ): number {
  if ( layer.type === 'text' ) return ptToMm( layer.font.size );

  return Math.max( 1, Math.min( layer.w, layer.h ) * 0.35 );
}

/**
 * The shadow as it was stored before effects existed.
 *
 * Kept working, and drawn exactly as it always was - a hard copy at a fixed
 * offset in millimetres - because saved designs have them and a design must
 * not change by being opened. An effect, once chosen, replaces it.
 */
function shadowPass( layer: Layer ): Pass | null {
  if ( layer.type !== 'shape' || ! layer.shadow || layer.effect ) return null;

  return {
    source: 'outline',
    dx: layer.shadow.x,
    dy: layer.shadow.y,
    fill: layer.shadow.colour,
    stroke: null,
    width: 0,
    alpha: layer.shadow.opacity,
    blur: 0,
    repeat: 1,
  };
}

/* ---------------------------------------------------------------
 * Starting points
 * ------------------------------------------------------------ */

/**
 * An effect as it first appears when chosen, before anything is adjusted.
 * Coloured from the object where that is what the effect means - a neon tube
 * glows in its own colour - and otherwise from sensible inks.
 */
export function startingEffect( kind: EffectKind, own: Colour ): Effect {
  const base: Effect = {
    kind,
    direction: 45,
    offset: 50,
    blur: 0,
    size: 50,
    roundness: 50,
    transparency: 0,
    colour: BLACK,
  };

  switch ( kind ) {
    case 'drop':
      return { ...base, blur: 40, transparency: 60 };
    case 'glow':
      return { ...base, colour: own, transparency: 40 };
    case 'echo':
      return { ...base, colour: own };
    case 'outline':
      return { ...base, size: 40, colour: BLACK };
    case 'background':
      return { ...base, size: 40, colour: pale( own ) };
    case 'splice':
      return { ...base, colour: pale( own ) };
    case 'hollow':
      return { ...base, size: 40 };
    case 'neon':
      return { ...base, colour: own };
    case 'glitch':
      return {
        ...base,
        direction: 0,
        offset: 30,
        colour: { space: 'cmyk', value: [ 1, 0, 0, 0 ] },
        colour2: { space: 'cmyk', value: [ 0, 1, 0, 0 ] },
      };
    default:
      return base;
  }
}

/** Which effects make sense on which objects. All of them do on text. */
export function effectsFor( layer: Layer ): EffectKind[] {
  const all: EffectKind[] = [ 'drop', 'glow', 'echo', 'outline', 'background', 'splice', 'hollow', 'neon', 'glitch' ];

  // A line has no inside to hollow out or splice.
  if ( layer.type === 'shape' && layer.shape === 'line' ) return [ 'drop', 'glow', 'echo', 'neon', 'glitch' ];

  return all;
}

/** The controls each effect has, in the order the panel shows them. */
export const CONTROLS: Record<EffectKind, Array<'direction' | 'offset' | 'blur' | 'size' | 'roundness' | 'transparency' | 'colour' | 'colour2'>> = {
  drop: [ 'direction', 'offset', 'blur', 'transparency', 'colour' ],
  glow: [ 'size', 'transparency', 'colour' ],
  echo: [ 'direction', 'offset', 'colour' ],
  outline: [ 'size', 'colour' ],
  background: [ 'roundness', 'size', 'transparency', 'colour' ],
  splice: [ 'size', 'offset', 'direction', 'colour' ],
  hollow: [ 'size' ],
  neon: [ 'size' ],
  glitch: [ 'offset', 'direction', 'colour', 'colour2' ],
};

/** What each control is called, which depends on the effect it belongs to. */
export function controlLabel( kind: EffectKind, control: string ): string {
  if ( control === 'size' ) {
    if ( kind === 'glow' ) return 'Size';
    if ( kind === 'background' ) return 'Spread';
    if ( kind === 'neon' ) return 'Intensity';

    return 'Thickness';
  }

  const names: Record<string, string> = {
    direction: 'Direction',
    offset: 'Offset',
    blur: 'Blur',
    roundness: 'Roundness',
    transparency: 'Transparency',
    colour: kind === 'glitch' ? 'Colour one' : 'Colour',
    colour2: 'Colour two',
  };

  return names[ control ] ?? control;
}

/* ---------------------------------------------------------------
 * Colour helpers
 * ------------------------------------------------------------ */

function solid( fill: Fill | null ): Colour | null {
  if ( ! fill ) return null;
  if ( isGradient( fill ) ) return fill.stops[ 0 ]?.colour ?? null;

  return fill;
}

/**
 * The colour, mostly paper. What a lit neon tube's letters look like, and a
 * background that sits quietly behind its text.
 */
export function pale( colour: Colour ): Colour {
  const ink: Ink = colour.space === 'cmyk' ? colour.value : colour.space === 'spot' ? colour.fallback : [ 0, 0, 0, 0.2 ];

  return { space: 'cmyk', value: ink.map( ( v ) => Math.round( v * 0.22 * 1000 ) / 1000 ) as Ink };
}

const clamp01 = ( v: number ) => Math.min( 1, Math.max( 0, v ) );
